import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthenticateVleUserSilentlyConfig,
  PersonaConfig,
} from '@technologyfromsage/persona-padlock';
import { NEVER, Observable, from, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as URI from 'urijs';

import { AuthenticationFailedException } from '®shared/auth/authentication-failed-exception';
import { AppConfig } from '®shared/config';
import { RedirectionService } from '®shared/redirection';
import { ErrorReportingService } from '®shared/reporting/error-reporting.service';

import { AuthProvider } from './auth-provider';
import { AuthServiceLogin } from './auth-service-login';
import { PersonaPadlockService } from './persona-padlock.service';
import { PersonaPkceSharedService } from './persona-pkce-shared.service';
import { PersonaUser, createPersonaUserFromPkceLogin } from './persona-user';
import { User } from './user';
import { UserScopesService } from './user-scopes.service';

@Injectable()
export class PersonaPkceLtiAuthService
  extends AuthProvider<User>
  implements AuthServiceLogin
{
  constructor(
    private errorReportingService: ErrorReportingService,
    private personaPadlockService: PersonaPadlockService,
    private personaPkceSharedService: PersonaPkceSharedService,
    private router: Router,
    appConfig: AppConfig,
    redirectionService: RedirectionService,
    userScopesService: UserScopesService,
  ) {
    super(appConfig, redirectionService, userScopesService);

    this.isInLti1p0Context = appConfig.isAnLti1p0EmbeddedSession;
  }

  private targetUri: URI | null = null;
  private isInLti1p0Context = false;

  public get postBreakoutLoginRedirectUri(): string {
    return this.targetUri.toString();
  }

  public obtainNewToken(returnUrl?: string): Observable<User> {
    const currentUri = returnUrl ? new URI(returnUrl) : this.getCurrentUri();

    const ltiAuthenticationRoute = '3/lti-authentication.html';

    this.targetUri = new URI(currentUri.resource());
    return from(this.router.navigateByUrl(ltiAuthenticationRoute)).pipe(
      switchMap(() => NEVER),
      catchError(error =>
        this.personaPkceSharedService.throwAuthenticationFailedException(error),
      ),
    );
  }

  public getCurrentToken(): Observable<User> {
    const currentUri = this.getCurrentUri();
    const authProvider = this.personaPkceSharedService.getAuthProvider(
      this.isTalisLoginUri(currentUri),
    );
    const config = this.personaPkceSharedService.getPersonaConfig(authProvider);

    if (this.isInLti1p0Context) {
      return this.getCurrentToken1p0(config);
    } else {
      return this.getCurrentToken1p3(currentUri, config);
    }
  }

  private getCurrentToken1p0(config: PersonaConfig) {
    const bypassFirstPartyCheck = true;

    return this.personaPkceSharedService.getCurrentToken(() =>
      this.personaPadlockService.silentlyAuthenticate(
        config,
        bypassFirstPartyCheck,
      ),
    );
  }

  private getCurrentToken1p3(currentUri: URI, config: PersonaConfig) {
    const ltiAuthNonce = currentUri.search(true).lti_auth_nonce;

    const ltiConfig: AuthenticateVleUserSilentlyConfig = {
      personaConfig: config,
      ltiAuthNonce,
      ltiAuthorizeUrl: `${this.appConfig.apiEndpoint}/lti/authorize`,
    };

    return this.personaPkceSharedService.getCurrentToken(() =>
      this.personaPadlockService.authenticateVleUserSilently(ltiConfig),
    );
  }

  public refreshToken(): Observable<User> {
    return this.personaPkceSharedService.refreshToken(this.currentUser);
  }

  public login(): void {
    const returnUrl = this.getCurrentUri();
    returnUrl.removeQuery('talis_login');

    this.obtainNewToken(returnUrl.toString());
  }

  public loginDuringRouteNavigation(stateUrl: string): void {
    const urlBase = this.getCurrentUri().origin();
    const returnUrl = new URL(stateUrl, urlBase);
    returnUrl.searchParams.delete('talis_login');

    this.obtainNewToken(returnUrl.toString());
  }

  public performLtiBreakoutLogin(): Observable<PersonaUser> {
    if (!this.targetUri) {
      const error = new AuthenticationFailedException(
        'Unable to authenticate: No target URI set, call obtainNewToken first',
      );

      this.errorReportingService.reportToInstana(error);
      return throwError(error);
    }

    const authProvider = this.personaPkceSharedService.getAuthProvider(
      this.isTalisLoginUri(this.targetUri),
    );
    const config = this.personaPkceSharedService.getPersonaConfig(authProvider);

    const breakoutFunction = this.isInLti1p0Context
      ? this.personaPadlockService.authenticateViaBreakout
      : this.personaPadlockService.authenticateVleUserViaBreakout;

    return breakoutFunction(config).pipe(
      map(loginResponse => createPersonaUserFromPkceLogin(loginResponse)),
    );
  }
}
